import { collection, deleteDoc, doc, getDoc, getDocs, limit, onSnapshot, orderBy, query, runTransaction, serverTimestamp, setDoc, startAfter, startAt, where, getCountFromServer } from "firebase/firestore";
import { Favorite } from "../models/Favorite";
import { Message } from "../models/Message";
import { Notification } from "../models/Notification";
import { RoomItem } from "../models/RoomItem";
import { User } from "../models/User";
import { Fire, FireClient, getFunc } from "./FireClient";
import { My } from "./My";
import { NotificationUtil } from "./Utils/NotificationUtil";
import { Util, notificationPojo } from "./Utils/Util";

export class FireClientMsg {
  public static NrMsgsPerLoad = 7;

  constructor(private c: FireClient) { }

  subscribeToNewMessages(userId: string, roomId: string, fromDate: Date | null, handler: (voiceMsg: Message[]) => void) {
    const conditions = [
      where("roomId", "==", roomId),
      where("userIds", 'array-contains', userId)
    ];

    if (fromDate) {
      conditions.push(where('created', '>', fromDate.toISOString()));
    }

    const q = query(collection(this.c.store, Message.COL), ...conditions, orderBy('created', 'desc'));
    const unsubscribe = onSnapshot(q, (snap) => {
      const list = snap.docs.map(d => d.data() as Message);

      handler(list);
    });
    return unsubscribe;
  }

  async saveMessageAndNotify(msg: Message, notifyOnlyMentioned: boolean = false) {
    await this.saveMessage(msg);
    this.notifyUsersOnNewMessage(msg, notifyOnlyMentioned);

    if (msg.text) {
      const match = msg.text?.match(/(https:\/\/|(www)\.)[-A-Z0-9+&@#\/%?=~_|$!:,.;]*[A-Z0-9+&@#\/%=~_|$]/ig);
      match?.forEach(url => {
        let item = new RoomItem(msg.roomId, msg.user);
        if (url.includes('officenote.co.kr')) {
          item.type = 'officenote';
        } else if (url.includes('pagecall.com')) {
          item.type = 'pagecall';
        }

        item.url = url.includes('http') ? url : `http://${url}`;
        item.message = msg;
        Fire.item.saveRoomItem(item);
      })
    }
    if (msg.files) {
      msg.files.forEach(file => {
        let item = new RoomItem(msg.roomId, msg.user);
        item.type = 'file';
        item.title = file.filename;
        item.url = file.url;
        item.message = msg;
        Fire.item.saveRoomItem(item);
      })
    }
  }

  async saveMessage(msg: Message): Promise<void> {
    let func = getFunc('saveMessage');
    await func({ message: Util.pojo(msg) });
  }

  async getLastMessage(userId: string, roomId: string): Promise<Message | undefined> {
    const conditions: any[] = [where("userIds", 'array-contains', userId), where("roomId", "==", roomId),
    orderBy('created', 'desc'), limit(1)];
    const q = query(collection(this.c.store, Message.COL), ...conditions);
    const snap = await getDocs(q);
    if (snap.docs.length > 0) {
      const msg = snap.docs[0].data() as Message;
      return msg;
    } else {
      return undefined;
    }
  }

  async notifyUsersOnNewMessage(msg: Message, notifyOnlyMentioned: boolean = false) {
    let usersToNotify: string[] = [];
    const room = My.Rooms.get(msg.roomId);

    const mentionedUserIds = Util.getMentionedUserIds(msg.text);
    const mentionsEveryone = mentionedUserIds.includes('everyone');

    if (!msg.parentMessageId) {
      if (mentionsEveryone) {
        usersToNotify = room?.userIds ?? [];
      } else if (mentionedUserIds.length > 0 && notifyOnlyMentioned) {
        usersToNotify = mentionedUserIds.map(id => id);
      } else {
        usersToNotify = room?.userIds ?? [];
      }
    } else {
      if (mentionsEveryone) {
        usersToNotify = room?.userIds ?? [];
      } else {
        usersToNotify = mentionedUserIds.map(id => id);
      }
    }

    if (msg.replyTo && !usersToNotify.find(id => id === msg.replyTo?.id)) {
      usersToNotify.push(msg.replyTo.id);
    }

    usersToNotify = usersToNotify.filter(id => (msg.user.id !== id && !id.startsWith('ai-') && id !== 'everyone'));
    let subtitle = Util.getTextAsPlain(msg.text).substring(0, 100) ?? '';
    if (subtitle.length > 100) {
      subtitle += '...';
    }

    try {
      await runTransaction(this.c.store, async (transaction) => {
        usersToNotify.forEach(userId => {
          let notif = NotificationUtil.createForMessage(msg, userId);
          notif.isMentioned = mentionsEveryone || !!mentionedUserIds.find(id => id === userId);
          notif.roomName = room?.name ?? '';
          notif.subtitle = subtitle;
          transaction.set(doc(this.c.store, Notification.COL, notif.id), notificationPojo(notif));
        })
      });
    } catch (e) {
      console.error("Notify users on new message failed: ", e);
    }
  }

  async getMessages(userId: string, roomId: string, lastMessageCreated: Date | null, limitCount: number = FireClientMsg.NrMsgsPerLoad): Promise<Message[]> {
    const conditions: any[] = [where('parentMessageId', '==', null), where("userIds", 'array-contains', userId), where("roomId", "==", roomId),
    orderBy('created', 'desc'), limit(limitCount)];
    if (lastMessageCreated)
      conditions.push(startAfter(lastMessageCreated.toISOString()))

    const q = query(collection(this.c.store, Message.COL), ...conditions);

    let parents = (await getDocs(q)).docs.map(d => d.data() as Message)
    return parents;
  }

  async getMessagesBetweenCreated(userId: string, roomId: string, lastMessageCreated: Date, fromCreatedDate: Date, limitCount: number = FireClientMsg.NrMsgsPerLoad): Promise<Message[]> {
    const conditions: any[] = [where('parentMessageId', '==', null), where("userIds", 'array-contains', userId), where("roomId", "==", roomId),
    where('created', '>=', fromCreatedDate.toISOString()), orderBy('created', 'desc'), startAt(lastMessageCreated.toISOString()), limit(limitCount)];
    const q = query(collection(this.c.store, Message.COL), ...conditions);

    let parents = (await getDocs(q)).docs.map(d => d.data() as Message)
    return parents;
  }

  async getMessagesAfter(userId: string, roomId: string, targetDate: Date): Promise<Message[]> {
    const conditions: any[] = [
      where('parentMessageId', '==', null),
      where("userIds", 'array-contains', userId),
      where("roomId", "==", roomId),
      where('created', '>', targetDate.toISOString()),
      orderBy('created', 'asc') // Order ascending to get messages chronologically after the target
    ];
    const q = query(collection(this.c.store, Message.COL), ...conditions);
    const snap = await getDocs(q);
    return snap.docs.map(d => d.data() as Message);
  }

  async getMessagesByParentIds(userId: string, ids: string[]): Promise<Message[]> {
    const cnt = ids.length;
    if (cnt > 0) {
      let messages: Message[] = [];
      for (let i = 0; i < Math.ceil(cnt / 30); i++) {
        const batchIds = ids.slice(i * 30, (i + 1) * 30);
        const q = query(collection(this.c.store, Message.COL), where('parentMessageId', 'in', batchIds), where("userIds", 'array-contains', userId), orderBy('created', 'asc'));
        const snap = await getDocs(q);
        messages = messages.concat(snap.docs.map(d => d.data() as Message));
      }
      return messages;
    } else {
      return [];
    }
  }

  async getChildrenMessages(userId: string, parentId: string): Promise<Message[]> {
    // parentMessageId로 모든 자식(답글 + 손자)을 한번에 로드
    const q = query(
      collection(this.c.store, Message.COL),
      where('parentMessageId', '==', parentId),
      where("userIds", 'array-contains', userId),
      orderBy('created', 'asc')
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => d.data() as Message);
  }

  async getChildSummary(userId: string, parentId: string): Promise<{ count: number, lastMessage: Message | null, reactions: Message[] }> {
    const baseQuery = query(
      collection(this.c.store, Message.COL),
      where('parentMessageId', '==', parentId),
      where("userIds", 'array-contains', userId)
    );

    const countPromise = getCountFromServer(baseQuery);
    const lastMsgQuery = query(baseQuery, orderBy('created', 'desc'), limit(1));
    const lastMsgPromise = getDocs(lastMsgQuery);

    // 🔧 reactions(이모지 반응)도 함께 로드 - 스레드 열기 전에도 표시하기 위함
    const reactionsQuery = query(
      collection(this.c.store, Message.COL),
      where('parentMessageId', '==', parentId),
      where("userIds", 'array-contains', userId),
      where('emoticon', '!=', null)
    );
    const reactionsPromise = getDocs(reactionsQuery);

    const [countSnap, lastMsgSnap, reactionsSnap] = await Promise.all([countPromise, lastMsgPromise, reactionsPromise]);

    return {
      count: countSnap.data().count,
      lastMessage: lastMsgSnap.docs.length > 0 ? lastMsgSnap.docs[0].data() as Message : null,
      reactions: reactionsSnap.docs.map(d => d.data() as Message)
    };
  }

  async getMessage(msgId: string): Promise<Message | undefined> {
    try {
      let d = await getDoc(doc(this.c.store, Message.COL, msgId));
      return d.data() as Message;
    } catch (error) {
      console.error('Failed to get message', error);
      return undefined;
    }
  }

  async deleteMessage(messageId: string) {
    // 자식 메시지(댓글) 존재 여부 확인 (emoticon 리액션 제외)
    const childrenQuery = query(
      collection(this.c.store, Message.COL),
      where('parentMessageId', '==', messageId)
    );
    const childrenSnap = await getDocs(childrenQuery);

    // emoticon이 없는 자식 메시지(일반 댓글)가 있는지 확인
    const hasRealChildren = childrenSnap.docs.some(doc => {
      const childMsg = doc.data() as Message;
      return !childMsg.emoticon;
    });

    if (hasRealChildren) {
      throw new Error('Cannot delete this message. Please delete replies first.');
    }

    await deleteDoc(doc(this.c.store, Message.COL, messageId));
  }

  async getFavorites(userId: string, roomId: string | null): Promise<Favorite[]> {
    const col = collection(this.c.store, User.COL, userId, Favorite.COL);
    const order = orderBy('created', 'desc');
    let q = roomId ? query(col, where("msg.roomId", "==", roomId), order) : query(col, order);

    let snap = await getDocs(q);
    return snap.docs.map(d => d.data() as Favorite);
  }

  async saveFavorite(fav: Favorite) {
    await setDoc(doc(this.c.store, User.COL, fav.userId, Favorite.COL, fav.id), Util.pojo(fav));
  }

  async deleteFavorite(userId: string, msgId: string) {
    await deleteDoc(doc(this.c.store, User.COL, userId, Favorite.COL, msgId));
  }

}

